import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { SectorBadge } from "@/components/Badges";
import api from "@/lib/api";
import { goldBtnSm, SECTORS } from "@/lib/constants";

export default function Deals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = () => api.get("/deals").then((r) => setDeals(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const requestAccess = async (e, id) => {
    e.stopPropagation();
    try {
      await api.post("/deals/request-access", { deal_id: id });
      toast.success("Access requested. The desk will review shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request access.");
    }
  };

  const shown = filter === "all" ? deals : deals.filter((d) => d.sector === filter);

  return (
    <PortalLayout title="Deal Board">
      <p className="text-sm text-muted-foreground max-w-2xl mb-6">
        A curated selection of off-market opportunities. Restricted deals show a teaser until the desk grants you access.
      </p>
      <div className="flex flex-wrap gap-2 mb-8">
        {[["all", "All sectors"], ...Object.entries(SECTORS)].map(([k, v]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`deal-filter-${k}`}
            className={`px-3 h-9 rounded-lg text-xs border transition-colors ${filter === k
              ? "border-[hsl(var(--pdg-gold)/0.5)] text-[hsl(var(--pdg-gold))] bg-[hsl(var(--pdg-gold)/0.08)]"
              : "border-border text-foreground/70 hover:text-foreground"}`}>{v}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading deals…</p>
      ) : shown.length === 0 ? (
        <GlassCard className="p-10 text-center text-sm text-muted-foreground">No deals in this sector currently.</GlassCard>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {shown.map((d) => (
            <GlassCard key={d.id} hover data-testid="deal-card" onClick={() => navigate(`/deals/${d.id}`)} className="p-6 cursor-pointer flex flex-col">
              <div className="flex items-center justify-between">
                <SectorBadge sector={d.sector} />
                {d.restricted && !d.has_access && (
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><Lock size={12} /> Restricted</span>
                )}
              </div>
              <h3 className="mt-4 pdg-serif text-xl text-foreground leading-snug">{d.title}</h3>
              <div className="mt-1 text-xs text-muted-foreground">{d.deal_type} &middot; {d.geography}</div>
              <p className="mt-3 text-sm text-muted-foreground leading-7 flex-1">{d.teaser}</p>
              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-foreground/85">{d.size_range}</span>
                {d.restricted && !d.has_access ? (
                  <button onClick={(e) => requestAccess(e, d.id)} data-testid="deal-card-request-access-button" className={goldBtnSm}>
                    Request Access
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-[hsl(var(--pdg-gold))]">View detail <ArrowRight size={14} /></span>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
